<template>
  <Teleport to="body">
    <div v-if="show" class="modal-overlay" @click.self="close">
      <div class="modal-content">
        <h4 class="editor-modal__name">
          {{ isEditing ? "Edit Resource" : "Add Another Resource" }}
        </h4>

        <div class="modal-body">
          <div class="d-flex flex-column justify-content-center mt-5">
            <p class="editor-modal__title">Attach a Resource Link</p>
            <p class="editor-modal__description">
              Please attach a link to your resource. Remember to make sure the
              resource is accessible to your students!
            </p>
          </div>
          <div class="mt-3">
            <div class="input-group">
              <div class="input-wrapper">
                <input
                  type="text"
                  id="materialTitle"
                  v-model="form.title"
                  placeholder="Give your resource a title. This is what the end-user will see."
                  @input="validateTitle"
                />
                <img
                  src="/images/icon/text.svg"
                  alt="Text"
                  class="input-icon"
                />
              </div>
              <div v-if="errors.title" class="error-message">
                {{ errors.title }}
              </div>
            </div>

            <div class="input-group">
              <div class="input-wrapper">
                <input
                  type="text"
                  id="materialUrl"
                  v-model="form.url"
                  placeholder="Attach the link here"
                  @input="validateUrl"
                />
                <img
                  src="/images/icon/link.png"
                  alt="Link"
                  class="input-icon"
                />
              </div>
              <div v-if="errors.url" class="error-message">
                {{ errors.url }}
              </div>
            </div>
          </div>

          <!-- Submit Status Messages -->
          <div v-if="submitState.error" class="status-message error-status">
            <div class="status-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>
            <div class="status-content">
              <strong>Oops! Something went wrong</strong>
              <p>{{ submitState.error }}</p>
            </div>
          </div>

          <div v-if="submitState.success" class="status-message success-status">
            <div class="status-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <div class="status-content">
              <strong>Success!</strong>
              <p>
                {{ isEditing ? "Resource updated" : "Resource added" }}
                successfully!
              </p>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="btn-cancel"
            @click="close"
            :disabled="submitState.isLoading"
          >
            Cancel
          </button>
          <button
            class="btn-submit"
            @click="handleSubmit"
            :disabled="!isFormValid || submitState.isLoading"
            :class="{
              loading: submitState.isLoading,
              success: submitState.success,
            }"
          >
            <span v-if="submitState.isLoading" class="loading-spinner"></span>
            <span v-else-if="submitState.success" class="success-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </span>
            <span class="button-text">
              {{
                submitState.isLoading
                  ? "Processing..."
                  : submitState.success
                    ? "Done!"
                    : "Confirm"
              }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import lessonPlanService from "@/services/lessonPlanService";
import professionalDevelopmentService from "@/services/professionalDevelopmentService";
import makercartActivityService from "@/services/makercartActivityService";
import { CATEGORY_TYPES } from "@/utils/constants";
import { computed, defineEmits, defineProps, reactive, watch } from "vue";
import { useRoute } from "vue-router";

const props = defineProps({
  id: {
    type: Number,
    default: null,
  },
  show: {
    type: Boolean,
    required: true,
  },
  editingIndex: {
    type: Number,
    default: null,
  },
  currentMaterial: {
    type: Object,
    default: null,
  },
  isEditForm: {
    type: Boolean,
    default: false,
  },
  moduleType: {
    type: String,
    default: "",
    required: true,
    validator: (value) => {
      return [
        CATEGORY_TYPES.LessonPlan,
        CATEGORY_TYPES.ProfessionalDevelopment,
        CATEGORY_TYPES.LessonMaterials,
      ].includes(value);
    },
  },
});

const route = useRoute();
const id = route.params.id;

const emit = defineEmits(["close", "add", "update"]);

const isEditing = computed(() => props.currentMaterial !== null);

const materialId = computed(() => props.currentMaterial?.id || null);

const form = reactive({
  title: "",
  url: "",
});

const errors = reactive({
  title: "",
  url: "",
});

const submitState = reactive({
  isLoading: false,
  error: "",
  success: false,
});

// Watch for changes to currentMaterial and populate form
watch(
  () => props.currentMaterial,
  (newVal) => {
    if (newVal) {
      form.title = newVal.title || "";
      form.url = newVal.url || "";
    }
  },
  { immediate: true }
);

// Watch for when modal is shown/hidden
watch(
  () => props.show,
  (newVal) => {
    if (!newVal) {
      // Reset form when modal is closed if not editing
      if (!isEditing.value) {
        resetForm();
      }
    }
  }
);

const resetForm = () => {
  form.title = "";
  form.url = "";
  errors.title = "";
  errors.url = "";
  submitState.isLoading = false;
  submitState.error = "";
  submitState.success = false;
};

const validateTitle = () => {
  if (!form.title.trim()) {
    errors.title = "Title is required";
    return false;
  }
  errors.title = "";
  return true;
};

const validateUrl = () => {
  if (!form.url.trim()) {
    errors.url = "URL is required";
    return false;
  }

  // Google services URL validation patterns
  const googleSheetsPattern =
    /^https:\/\/docs\.google\.com\/spreadsheets\/d\/[a-zA-Z0-9-_]+/;
  const googleDocsPattern =
    /^https:\/\/docs\.google\.com\/document\/d\/[a-zA-Z0-9-_]+/;
  const googleSlidesPattern =
    /^https:\/\/docs\.google\.com\/presentation\/d\/[a-zA-Z0-9-_]+/;
  const googleDrivePattern =
    /^https:\/\/drive\.google\.com\/(file\/d\/[a-zA-Z0-9-_]+|drive\/folders\/[a-zA-Z0-9-_]+)/;

  const url = form.url.trim();

  const isValidGoogleUrl =
    googleSheetsPattern.test(url) ||
    googleDocsPattern.test(url) ||
    googleSlidesPattern.test(url) ||
    googleDrivePattern.test(url);

  if (!isValidGoogleUrl) {
    errors.url =
      "Please enter a valid Google Sheets, Docs, Slides, or Drive link";
    return false;
  }

  errors.url = "";
  return true;
};

const validateForm = () => {
  const isTitleValid = validateTitle();
  const isUrlValid = validateUrl();

  return isTitleValid && isUrlValid;
};

const isFormValid = computed(() => {
  return (
    form.title.trim() !== "" &&
    form.url.trim() !== "" &&
    !errors.title &&
    !errors.url
  );
});

const createMaterial = async (material) => {
  if (props.moduleType === CATEGORY_TYPES.LessonPlan) {
    return lessonPlanService.createMaterial(material);
  } else if (props.moduleType === CATEGORY_TYPES.ProfessionalDevelopment) {
    return professionalDevelopmentService.createMaterial(material);
  } else if (props.moduleType === CATEGORY_TYPES.LessonMaterials) {
    return makercartActivityService.createMaterial(material);
  }
};

const updateMaterial = async (materialId, material) => {
  if (props.moduleType === CATEGORY_TYPES.LessonPlan) {
    return lessonPlanService.updateMaterials(materialId, material);
  } else if (props.moduleType === CATEGORY_TYPES.ProfessionalDevelopment) {
    return professionalDevelopmentService.updateMaterials(materialId, material);
  } else if (props.moduleType === CATEGORY_TYPES.LessonMaterials) {
    return makercartActivityService.updateMaterials(materialId, material);
  }
};

const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  // Reset previous states
  submitState.error = "";
  submitState.success = false;
  submitState.isLoading = true;

  const material = {
    title: form.title.trim(),
    url: form.url.trim(),
  };

  try {
    if (isEditing.value) {
      // Update existing material
      if (props.isEditForm && materialId.value) {
        const updateData = {
          name: form.title.trim(),
          url: form.url.trim(),
          order: props.editingIndex,
        };

        const response = await updateMaterial(materialId.value, updateData);

        if (response && response.status === 200) {
          submitState.success = true;
          setTimeout(() => {
            emit("update", props.editingIndex, {
              ...material,
              id: materialId.value,
            });
            close();
          }, 500); // Brief delay to show success state
        } else {
          throw new Error("Failed to update material");
        }
      } else {
        // For non-edit forms, just emit the update
        emit("update", props.editingIndex, {
          ...material,
          ...(materialId.value ? { id: materialId.value } : {}),
        });
        close();
      }
    } else {
      // Create new material
      if (props.isEditForm && id) {
        const createData = {
          name: form.title.trim(),
          url: form.url.trim(),
          order: props.editingIndex,
        };

        if (props.moduleType === CATEGORY_TYPES.LessonPlan) {
          createData.lesson_id = Number(id);
        } else if (
          props.moduleType === CATEGORY_TYPES.ProfessionalDevelopment
        ) {
          createData.instructional_strategies_id = Number(id);
        } else if (props.moduleType === CATEGORY_TYPES.LessonMaterials) {
          createData.educator_guide_id = Number(id);
        }

        const response = await createMaterial(createData);

        if (response.data) {
          const newMaterial = response.data.data;
          material.id = newMaterial?.id;
          submitState.success = true;

          setTimeout(() => {
            emit("add", material);
            close();
          }, 500); // Brief delay to show success state
        } else {
          throw new Error("Failed to create material");
        }
      } else {
        // For non-edit forms, just emit the add
        emit("add", material);
        close();
      }
    }
  } catch (error) {
    console.error("Error processing material:", error);

    // Set user-friendly error messages
    if (error.response) {
      // API error with response
      const status = error.response.status;
      const message = error.response.data?.message || "Unknown error occurred";

      if (status === 400) {
        submitState.error =
          "Invalid data provided. Please check your inputs and try again.";
      } else if (status === 401) {
        submitState.error =
          "You are not authorized to perform this action. Please sign in again.";
      } else if (status === 403) {
        submitState.error =
          "You don't have permission to modify this resource.";
      } else if (status === 404) {
        submitState.error =
          "The resource you're trying to update was not found.";
      } else if (status === 422) {
        submitState.error = `Validation error: ${message}`;
      } else if (status >= 500) {
        submitState.error = "Server error occurred. Please try again later.";
      } else {
        submitState.error = `Error: ${message}`;
      }
    } else if (error.request) {
      // Network error
      submitState.error =
        "Network error. Please check your connection and try again.";
    } else {
      // Other errors
      submitState.error =
        error.message || "An unexpected error occurred. Please try again.";
    }
  } finally {
    submitState.isLoading = false;
  }
};

const close = () => {
  resetForm();
  emit("close");
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content h3 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 1.25em;
  color: #343a40;
}

.modal-body {
  margin-bottom: 20px;
}

.input-group {
  margin-bottom: 1.5rem;
}

.input-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.input-group input,
.input-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
}

.input-wrapper {
  position: relative;
  width: 100%;
}

.input-wrapper input {
  padding-left: 40px; /* Make room for the icon */
  width: 100%;
}

.input-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
}

.input-group textarea {
  resize: vertical;
  min-height: 80px;
}

.error-message {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.material-type-selector {
  display: flex;
  gap: 12px;
  margin-bottom: 5px;
}

.type-option {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.type-option:hover {
  background-color: #f8f9fa;
  border-color: #adb5bd;
}

.type-option.active {
  background-color: #e6f7ff;
  border-color: var(--primary-blue);
}

.type-icon {
  width: 32px;
  height: 32px;
  margin-bottom: 8px;
}

.type-option span {
  font-size: 0.85rem;
  font-weight: 500;
  text-align: center;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  background-color: #f8f9fa;
  border: 1px solid #ced4da;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.btn-submit {
  padding: 0.5rem 1rem;
  background-color: var(--primary-blue, #0080ff);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.btn-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-submit.loading {
  background-color: #6c757d;
  cursor: wait;
}

.btn-submit.success {
  background-color: #28a745;
  animation: pulse-success 0.6s ease-in-out;
}

@keyframes pulse-success {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.btn-submit {
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.success-icon {
  display: flex;
  align-items: center;
}

.button-text {
  display: flex;
  align-items: center;
}

/* Status Messages */
.status-message {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  margin-top: 20px;
  margin-bottom: 20px;
  animation: slideInFromTop 0.4s ease-out;
}

@keyframes slideInFromTop {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.error-status {
  background-color: #fff5f5;
  border: 1px solid #feb2b2;
  color: #c53030;
}

.success-status {
  background-color: #f0fff4;
  border: 1px solid #9ae6b4;
  color: #2f855a;
}

.status-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.status-content {
  flex: 1;
}

.status-content strong {
  display: block;
  font-weight: 600;
  margin-bottom: 4px;
  font-size: 0.95rem;
}

.status-content p {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
  opacity: 0.9;
}
</style>
